export const COUNTRY_TIMEZONES = Object.freeze({US:'America/New_York',GB:'Europe/London',UK:'Europe/London',IE:'Europe/Dublin',CA:'America/Toronto',AU:'Australia/Sydney',NZ:'Pacific/Auckland'});

function zonedParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-CA', {timeZone,year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'});
  const parts = Object.fromEntries(formatter.formatToParts(date).filter(part => part.type !== 'literal').map(part => [part.type,Number(part.value)]));
  return parts;
}

export function nextMidnight(timeZone, now = new Date()) {
  const today = zonedParts(now, timeZone);
  const tomorrow = new Date(Date.UTC(today.year,today.month-1,today.day+1));
  const targetWall = Date.UTC(tomorrow.getUTCFullYear(),tomorrow.getUTCMonth(),tomorrow.getUTCDate());
  let epoch = targetWall;
  for (let pass = 0; pass < 4; pass++) {
    const local = zonedParts(new Date(epoch),timeZone);
    const seenWall = Date.UTC(local.year,local.month-1,local.day,local.hour,local.minute,local.second);
    const delta = targetWall - seenWall;
    epoch += delta;
    if (delta === 0) break;
  }
  return new Date(epoch);
}

export function resolveTimerEnd({endsAt,timeZone,country}, now = new Date()) {
  // A dated offer expiry wins. Country-zone midnight is only the existing daily offer.
  if (endsAt) {
    const explicit = new Date(endsAt);
    if (Number.isFinite(explicit.getTime())) return explicit;
  }
  const zone = timeZone || COUNTRY_TIMEZONES[String(country || '').toUpperCase()];
  if (!zone) return null;
  try { return nextMidnight(zone,now); } catch { return null; }
}

export function timerParts(end, now = new Date()) {
  const seconds = Math.max(0,Math.floor((end.getTime()-now.getTime())/1000));
  return {hours:String(Math.floor(seconds/3600)).padStart(2,'0'),minutes:String(Math.floor(seconds%3600/60)).padStart(2,'0'),seconds:String(seconds%60).padStart(2,'0'),expired:seconds===0};
}

export function initTimers(document, now = () => new Date()) {
  for (const element of document.querySelectorAll('.ed-timer')) {
    const country = element.dataset.country || element.closest('[data-country]')?.dataset.country || document.body.dataset.country;
    const end = resolveTimerEnd({country,timeZone:element.dataset.timezone,endsAt:element.dataset.endsAt},now());
    if (!end) { element.textContent = 'Ends midnight'; continue; }
    element.dataset.resolvedEndsAt = end.toISOString();
    const tick = () => {
      const parts = timerParts(end,now());
      const value = element.querySelector('[data-timer-value]');
      if (value) value.textContent = `${parts.hours}H ${parts.minutes}M ${parts.seconds}S`;
      else if (element.querySelector('[data-timer-hours]')) {
        for (const key of ['hours','minutes','seconds']) {
          const span = element.querySelector(`[data-timer-${key}]`);
          if (span) span.textContent = parts[key];
        }
      } else element.textContent = `${parts.hours}H ${parts.minutes}M ${parts.seconds}S`;
      element.setAttribute('aria-label',parts.expired?'Offer deadline reached':`Offer ends at midnight: ${parts.hours} hours ${parts.minutes} minutes remaining`);
      element.dataset.expired = String(parts.expired);
      return parts.expired;
    };
    if (!tick()) {
      const interval = setInterval(() => { if (tick()) clearInterval(interval); },1000);
      // The deadline does not restart when it reaches zero.
      document.defaultView?.addEventListener('pagehide',() => clearInterval(interval),{once:true});
    }
  }
}

export function initSliders(document) {
  for (const slider of document.querySelectorAll('.ed-slider')) {
    const track = slider.querySelector('.ed-slider-track');
    const cards = track ? Array.from(track.querySelectorAll('.ed-review-card')) : [];
    if (!cards.length) continue;
    slider.setAttribute('role','region');
    if (!slider.hasAttribute('aria-label')) slider.setAttribute('aria-label','Review examples');
    track.tabIndex = 0;
    const previous = slider.querySelector('[data-slider-prev]');
    const next = slider.querySelector('[data-slider-next]');
    const status = slider.querySelector('[data-slider-status]');
    const reduced = document.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const nearest = () => {
      const origin = track.getBoundingClientRect().left;
      let index = 0, distance = Infinity;
      cards.forEach((card,i) => {
        const current = Math.abs(card.getBoundingClientRect().left-origin-20);
        if (current < distance) {index=i;distance=current;}
      });
      return index;
    };
    const update = () => {
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= track.scrollWidth-track.clientWidth-2;
      if (status) status.textContent = `${nearest()+1} / ${cards.length}`;
    };
    const go = index => {
      const target = cards[Math.max(0,Math.min(cards.length-1,index))];
      const left = track.scrollLeft+target.getBoundingClientRect().left-track.getBoundingClientRect().left-20;
      track.scrollTo({left:Math.max(0,left),behavior:reduced?'auto':'smooth'});
    };
    previous?.addEventListener('click',() => go(nearest()-1));
    next?.addEventListener('click',() => go(nearest()+1));
    track.addEventListener('keydown',event => {
      const targets = {ArrowLeft:nearest()-1,ArrowRight:nearest()+1,Home:0,End:cards.length-1};
      if (Object.hasOwn(targets,event.key)) {event.preventDefault();go(targets[event.key]);}
    });
    track.addEventListener('scroll',update,{passive:true});
    if (document.defaultView?.ResizeObserver) new document.defaultView.ResizeObserver(update).observe(track);
    update();
  }
}

if (typeof document !== 'undefined') {
  const boot = () => {initTimers(document);initSliders(document);};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot,{once:true});
  else boot();
}
